import apiConfig from '../config/apiConfig';

const base = apiConfig.baseUrl;

export interface CreatorRefreshStatus {
  status: 'on_demand' | 'scheduled' | 'queued' | 'checking' | 'analyzing' | 'waiting_batch' | 'partial' | 'error' | 'limited' | 'profile_only';
  automatic: boolean;
  pending_posts?: number;
  failed_posts?: number;
  posts_checked?: number;
  last_checked?: string | null;
  last_completed?: string | null;
  next_check?: string | null;
  interval_hours?: number | null;
  message?: string | null;
  batch_window_hours?: number;
  manual_job_id?: string | null;
  manual_status?: 'queued' | 'running' | 'complete' | 'failed' | 'interrupted' | null;
}

export interface CreatorPlatformLink {
  platform: string;
  handle: string;
  target_creator_id: string;
  status: 'pending' | 'linked' | 'failed';
  message?: string | null;
  updated_at: string;
}

export interface CreatorSummary {
  is_demo?: boolean;
  id: string;
  platform: string;
  handle: string;
  name: string;
  avatar_url: string | null;
  local_avatar: string | null;
  bio: string | null;
  followers_count: number | null;
  sponsorship_count: number;
  last_scraped: string | null;
  person_id: string | null;
  person_display_name: string | null;
  person_avatar: string | null;
  // Fields the backend already returns but the frontend type didn't
  // expose. Populated for Instagram creators today; YouTube creators
  // typically have these null until subscriber-count persistence ships.
  is_verified?: boolean | null;
  is_business?: boolean | null;
  business_category?: string | null;
  platform_id?: string | null;
  profile_url?: string | null;
  // YouTube channel stats — persisted as of migration 0012. Null for
  // IG creators (they use ``followers_count``) and for YT creators
  // whose stats haven't been backfilled yet.
  subscriber_count?: number | null;
  view_count?: number | null;
  video_count?: number | null;
  channel_description?: string | null;
  // Per-creator preview data surfaced by the list endpoint so the home
  // grid cards can show real brand logos + a discount-code signal without
  // a second round-trip. Brand logos/colors are already resolved at
  // persist time (guess_brand_logo) — these are just surfaced here. Capped
  // at 5 brands; ``code_count`` counts verified "code"-type sponsorships
  // with a usable value (the follower-facing payload).
  top_brands?: { name: string; logo_url: string | null; brand_color: string | null }[];
  code_count?: number;
  // Content-DNA (migration 0027): a distilled summary of what the creator
  // actually makes content about + topic tags, from their real videos.
  content_summary?: string | null;
  content_topics?: string[] | null;
}

/** Deterministic signals from the stored recent-uploads window (migration 0027). */
export interface VideoInsights {
  videos_stored: number;
  uploads_per_month?: number;
  last_upload?: string;
  top_video?: {
    title: string | null;
    url: string | null;
    view_count: number | null;
    thumbnail_url: string | null;
  };
}

export interface BrandSummary {
  id: string;
  name: string;
  normalized_name: string;
  category: string;
  logo_url: string | null;
  brand_color: string | null;
  website: string | null;
  sponsorship_count: number;
  creator_count: number;
  first_seen: string | null;
  last_seen: string | null;
}

export interface ContentSource {
  id: string;
  sponsorship_id: string;
  platform: string;
  content_id: string;
  url: string;
  title: string | null;
  thumbnail_url: string | null;
  published_at: string | null;
  /** Post engagement — real scraped numbers (IG Apify; YT official API
   *  statistics since 2026-07-19). Null when unavailable — render
   *  conditionally, never fabricate. IG likes can be -1 when hidden. */
  likes_count?: number | null;
  comments_count?: number | null;
  views_count?: number | null;
  /** IG posts store their text here (YT uses `title`). */
  caption?: string | null;
}

/** Frozen payload behind /share/report/:token — same shape the profile
 *  page composes, minus app chrome (claims/votes). */
export interface PublicReportSnapshot {
  creator: Record<string, any>;
  sponsorships: SponsorshipDetail[];
  person_id: string | null;
  other_platforms: Record<string, any>[];
  contact_email?: string;
}

/** A scraped merch product (one tile in the Shop section). */
export interface MerchProduct {
  id: string;
  shop_domain: string;
  product_handle: string;
  title: string;
  price_min: number | null;
  price_max: number | null;
  currency: string | null;
  image_url: string | null;
  product_url: string;
  available: boolean;
}

export interface SponsorshipDetail {
  id: string;
  brand_name: string;
  opportunity_type: string;
  value: string;
  offer_text: string;
  context: string;
  category: string;
  /** Re-graded by the deterministic confidence scorer at read time.
   *  One of "Very Low" | "Low" | "Medium" | "High". */
  confidence: string;
  /** Float 0..1 — populated when the backend has re-scored the row. */
  confidence_score?: number;
  /** Names of the signals that contributed to the score, e.g.
   *  ["explicit_sponsorship_marker", "personalised_affiliate_url"]. */
  confidence_reasons?: string[];
  /** Set by the false-positive filter. When false, the backend has flagged
   *  this row as a probable non-brand or "thanks to" misattribution. */
  is_likely_brand?: boolean;
  drop_reason?: string | null;
  is_creator_owned: boolean;
  retailers: string[];
  evidence_types: string[];
  brand_logo_url: string | null;
  brand_color: string | null;
  brand_website?: string | null;
  first_seen: string | null;
  last_seen: string | null;
  computed_expiration: string | null;
  time_scraped: string | null;
  source_method: string | null;
  content_sources: ContentSource[];
  content_id: string | null;
  content_url: string | null;
  content_title: string | null;
  content_thumbnail: string | null;
  published_at: string | null;
}

export interface ProspectRecommendation {
  brand_name: string;
  fit_score: number;
  reasoning: string;
  pitch_angle: string;
  category: string;
  existing_sponsors_in_category: string[];
}

export interface StatsResponse {
  creators: number;
  brands: number;
  sponsorships: number;
  platforms: Record<string, number>;
  categories: Record<string, number>;
}

export interface MyProspectsRequest {
  name?: string;
  niche: string;
  platforms?: string[];
  audience_size?: string;
  existing_sponsors?: string[];
  content_style?: string;
  max_results?: number;
}

/** One annotated pitch paragraph — its strategic job + the real data behind it. */
export interface PitchSection {
  /** 2-4 word name for the section's job, e.g. "Proof, not adjectives". */
  label: string;
  /** The paragraph text itself (part of the ready-to-send email). */
  body: string;
  /** One coach-style sentence on why this section sits here. */
  strategy: string;
  /** The real profile/brand facts this paragraph was built from. */
  data_used: string[];
}

export interface ResearchSource { url: string; title?: string; creator_name?: string; observed_at?: string }
export interface PitchResearch { run_id: string; brand: string; sources: ResearchSource[]; scope: string }

export interface PitchResponse {
  research_context?: PitchResearch | null;
  pitch: string;
  /** Annotated sections (new shape). Null/absent → render `pitch` as plain text. */
  sections?: PitchSection[] | null;
  subject_line?: string;
  creator: string;
  brand: string;
  pitch_type: string;
  draft_id?: string | null;
}

export interface ProspectRunSummary {
  id: string;
  mode: 'explore' | 'my_creator' | 'similar_to_brand';
  summary: string;
  recommendations_count: number;
  created_at: string | null;
}

export interface ProspectRunDetail {
  id: string;
  mode: 'explore' | 'my_creator' | 'similar_to_brand';
  input_payload: Record<string, unknown>;
  output_payload: Record<string, unknown>;
  created_at: string | null;
}

export interface PastPitchSummary {
  research_context?: PitchResearch | null;
  send_attempted?: boolean;
  id: string;
  gmail_thread_id?: string | null;
  sent_at?: string | null;
  outcome?: string | null;
  creator_handle: string | null;
  creator_platform: string | null;
  brand_name: string;
  pitch_type: string;
  subject: string | null;
  body: string;
  preview: string;
  created_at: string | null;
}

// ─── Outreach pipeline (Drafted → Sent → Awaiting → Replied → Won/Lost) ────
export type OutreachStage = 'drafted' | 'delivery_check' | 'awaiting' | 'replied' | 'bounced' | 'won' | 'lost';

export interface OutreachCard {
  id: string;
  brand_name: string;
  /** Real scraped logo, resolved server-side by exact normalized brand key.
   *  Null when we have no brand row on file — the UI shows an initial. */
  brand_logo: string | null;
  brand_color: string | null;
  /** Creator-reported fee for a Won deal. null = not reported (never 0). */
  deal_amount?: number | null;
  deal_currency?: string | null;
  deal_deliverables?: string | null;
  creator_handle: string | null;
  creator_platform: string | null;
  pitch_type: string;
  subject: string | null;
  preview: string;
  body: string;
  created_at: string | null;
  sent_at: string | null;
  reply_status: 'no_reply' | 'replied' | 'bounced' | null;
  reply_synced_at?: string | null;
  reply_check_error?: string | null;
  last_reply_at: string | null;
  /** When THEY last wrote — distinct from last_reply_at, which moves when
   *  the creator replies too. */
  last_inbound_at?: string | null;
  /** What their reply meant, once classified. Null until then, and null is
   *  rendered as nothing — never as an "unclear" badge. */
  reply_intent?:
    | 'interested' | 'rates' | 'needs_info' | 'pass' | 'auto_reply' | 'unclear'
    | null;
  /** Their own opening words, trimmed. Never a paraphrase. */
  reply_snippet?: string | null;
  /** True when the in-app Gmail send linked a thread (auto reply-tracking). */
  gmail_tracked: boolean;
  send_attempted?: boolean;
  delivery_confirmed?: boolean;
  outcome: 'won' | 'lost' | null;
  outcome_at: string | null;
  /** Server-derived stage — single source of truth for grouping. */
  stage: OutreachStage;
}

/** A recent sent thread from the user's own Gmail (inbox browser). */
export interface OutreachInboxThread {
  thread_id: string;
  subject: string;
  to: string;
  snippet: string;
  message_count: number;
  started_at: string | null;
}

/** One message in a tracked pitch's Gmail conversation (plain text only). */
/** Send the creator's reply INTO the tracked Gmail thread, as them. */
export interface OutreachReplyResult {
  sent: boolean;
  message_id: string;
  to: string;
  subject: string;
}

export interface OutreachMessage {
  from: string;
  date: string | null;
  /** Derived server-side from the connected Gmail address, not from
   *  "whoever sent message zero" — imported threads don't always start
   *  with ours. */
  direction: 'outgoing' | 'incoming';
  body: string;
  /** RFC-822 Message-Id. The server threads replies itself; this is here so
   *  the client can tell messages apart without positional indexes. */
  message_id?: string | null;
  subject?: string | null;
  /** Gmail's stable message id (GET /api/outreach/{id}/thread). A question narrows the deal's email to messages by
   *  this id (email_message_ids) — never by message_id. Absent from an older server: then nothing can be picked. */
  id?: string;
}

export interface SimilarToBrandRequest {
  source_brand?: string;
  brand_id?: string;
  my_creator_handle?: string;
  my_platform?: string;
  count?: number;
}

export interface ProspectRecommendationFull {
  brand: string;
  brand_id?: string;
  category: string;
  score: number;
  /** Neutral 1-liner on what the brand sells (product-fit context). */
  description?: string;
  reasoning: string;
  pitch_angle: string;
  logo_url?: string;
  brand_color?: string;
  sponsorship_count?: number;
  creator_count?: number;
  shared_with_creators?: string[];
}

// ─── Sprint 9 — "Find their partnerships team" (replaces Hunter.io) ─────
//
// Legacy response shape for rolling deployments only. Never rendered or used.
export interface OutreachEmailPattern {
  address: string;
  label: string;
  confidence: 'common' | 'occasional';
}

export interface OutreachHints {
  contacts_status?: 'not_checked' | 'locked' | 'checked' | 'stale' | 'unavailable';
  primary_domain: string;
  email_patterns: OutreachEmailPattern[];
  /** Published contacts only. Consult contacts_status to distinguish empty,
   *  failed, unsearched, and tier-locked results. */
  found_contacts: { address: string; source: string }[];
  contacts_checked_at: string | null;
  linkedin_search_url: string;
  partnerships_page_url: string;
  /** True when partnerships_page_url was read off the brand's landing page
   *  (often an off-domain form host). Unverified URLs must not be displayed. */
  partnerships_page_verified?: boolean;
  tips: string[];
  /** Fields the server withheld for this tier (e.g. "email_patterns" on
      Free). The UI renders an honest lock row for these — never a blur. */
  tier_locked_fields?: string[];
  unlock_tier?: 'plus' | 'pro';
}

// ─── Sprint 9 Phase 1 — Bulk creator scrape + cross-platform linking ────
//
// Two new flows ride on these types:
//   1. POST /api/data/scrape-bulk — kicks off a multi-creator analysis job.
//      Frontend sends an array of {platform, handle} pairs (typically the
//      user's YouTube subscriptions); backend de-dupes against already-
//      analyzed creators and queues fresh ones. Returns a job_id we poll.
//   2. GET /api/data/scrape-bulk/{job_id}/progress — polled every 3s while
//      the job is `running`. Each per-creator row carries a list of
//      cross_platform_candidates the linking pass auto-discovered. Medium-
//      confidence ones (auto_merged: false) need user confirmation via
//      /api/data/creator-link-confirm; high-confidence ones are merged
//      backend-side and surface as auto_merged: true so the UI can simply
//      acknowledge them.

export type CreatorPlatform = 'youtube' | 'instagram' | 'tiktok' | 'twitch';

export type CrossPlatformCandidatePlatform = 'instagram' | 'tiktok' | 'twitch';

export interface CrossPlatformCandidate {
  platform: CrossPlatformCandidatePlatform;
  handle: string;
  confidence: 'high' | 'medium' | 'low';
  /** Backend has already merged the source + candidate into one Person row.
   *  true → no user confirmation needed (high-confidence match).
   *  false → surface the ambiguous-match modal to the user. */
  auto_merged: boolean;
  /** Optional metadata the backend may include to help the user decide.
   *  Not part of the strict contract — be defensive when reading. */
  display_name?: string;
  avatar_url?: string;
  follower_count?: number | null;
  /** Stable creator id used by /api/data/creator-link-confirm. */
  creator_id?: string;
}

export interface BulkScrapeCreatorRow {
  handle: string;
  platform: CreatorPlatform;
  status: 'queued' | 'in_progress' | 'done' | 'failed';
  sponsorships_found: number | null;
  cross_platform_candidates: CrossPlatformCandidate[];
  /** Optional fields the backend may include — be defensive when reading. */
  display_name?: string;
  avatar_url?: string;
  /** Stable creator id used as `source_creator_id` in /creator-link-confirm. */
  creator_id?: string;
  error?: string | null;
}

export interface BulkScrapeProgress {
  job_id: string;
  status: 'running' | 'completed' | 'failed';
  total: number;
  done: number;
  creators: BulkScrapeCreatorRow[];
}

export interface BulkScrapeStartResponse {
  job_id: string;
  queued: number;
  accepted: number;
  rejected: number;
  rejected_reasons: string[];
}

export interface CreatorLinkConfirmResponse {
  merged: boolean;
  person_id: string;
}

export interface CreatorLinkReportResponse {
  reported: boolean;
  total_reports: number;
}

/** Thrown when a quota-gated endpoint 403s (limit spent) — callers render
 *  the SpentQuotaCard recap instead of a generic "failed, try again". */
export class QuotaError extends Error {
  readonly isQuotaError = true as const;
  constructor(message: string) {
    super(message);
    this.name = 'QuotaError';
  }
}

/** Thrown when an endpoint answers 403 with X-Error-Type: tier_required — the server's plan gate (bulk-checking the
 *  channels you follow is Plus and up). The page says what the plan unlocks; it never pre-flights the gate itself. */
export class TierRequiredError extends Error {
  readonly isTierRequired = true as const;
  constructor(message: string) {
    super(message);
    this.name = 'TierRequiredError';
  }
}

async function handleResponse<T>(
  response: Response,
  opts?: { quotaGated?: boolean },
): Promise<T> {
  if (!response.ok) {
    let msg: string;
    let reason: string | undefined;
    try {
      const body = await response.json();
      // detail can be a string OR a structured dict ({message, ...}) — the
      // 402 tier-gate idiom uses the latter. Never let an object stringify
      // to "[object Object]".
      const d = body.detail;
      // Machine-readable failure codes (e.g. {reason: 'gmail_insufficient_scope'})
      // ride alongside the human message so callers can branch on the cause
      // instead of pattern-matching prose.
      reason = typeof d === 'object' && d ? d.reason : undefined;
      msg =
        (typeof d === 'string' ? d : d?.message) ||
        body.message ||
        response.statusText;
    } catch {
      msg = response.statusText;
    }
    // Only the quota-gated RUN endpoints translate a 403 into QuotaError —
    // a 403 elsewhere (ownership, admin) must not render as "quota spent".
    if (response.status === 403 && opts?.quotaGated) throw new QuotaError(msg);
    const err = new Error(msg) as Error & { status?: number; reason?: string };
    err.status = response.status;
    if (reason) err.reason = reason;
    throw err;
  }
  return response.json();
}

/** JSON headers + Bearer token. The prospect/pitch endpoints are
 *  Bearer-gated server-side — omitting the token 401s (this was the prod
 *  bug that broke the paid tabs: the FE never sent one). */
function authJsonHeaders(authToken?: string): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
  return headers;
}

const dataService = {
  getCreators: async (platform?: string): Promise<{ creators: CreatorSummary[]; total: number }> => {
    const params = platform ? `?platform=${encodeURIComponent(platform)}` : '';
    const res = await fetch(`${base}${apiConfig.endpoints.dataCreators}${params}`);
    return handleResponse(res);
  },

  getBrands: async (
    minSponsorships?: number,
    category?: string,
    options?: {
      /** When true, include the creator's own products (Chamberlain Coffee,
       *  SipMargs, etc.) — by default these are filtered out so the brands
       *  grid is only external sponsors useful for pitch-finding. */
      includeCreatorOwned?: boolean;
    },
  ): Promise<{
    brands: BrandSummary[];
    total: number;
    /** Number of brands hidden because every sponsorship of theirs is creator-owned. */
    creator_owned_filtered_count?: number;
  }> => {
    const params = new URLSearchParams();
    if (minSponsorships && minSponsorships > 0) params.set('min_sponsorships', String(minSponsorships));
    if (category) params.set('category', category);
    if (options?.includeCreatorOwned) params.set('exclude_creator_owned', 'false');
    const qs = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`${base}${apiConfig.endpoints.dataBrands}${qs}`);
    return handleResponse(res);
  },

  /** Individual scraped products from a creator's own merch shop (Shopify
   *  /products.json). Public, lazy + server-cached. */
  /** The platform accounts the signed-in user has claimed — lets the dashboard
   *  resolve "your creator" from the claim relationship (not just this browser's
   *  YouTube-OAuth localStorage), so a handle-claimed / seeded / demo creator's
   *  own data loads. Newest proof first. */
  getMyClaimedCreators: async (
    authToken?: string,
  ): Promise<{ creators: { platform: string; handle: string; provider: string }[] }> => {
    const res = await fetch(`${base}/api/data/my-claimed-creators`, {
      headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
    });
    return handleResponse(res);
  },

  getCreatorShopItems: async (
    platform: string,
    handle: string,
    // Optional: a signed-in caller's token. Only needed so the OWNER of a demo
    // creator can still load their (otherwise owner-gated) demo shop.
    authToken?: string,
  ): Promise<{ items: MerchProduct[]; count: number; fallback_shops: { domain: string; url: string }[] }> => {
    const res = await fetch(
      `${base}/api/data/creator/${encodeURIComponent(platform)}/${encodeURIComponent(handle)}/shop`,
      { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} },
    );
    return handleResponse(res);
  },

  // --- Creator-owned merch management (MERCH-ATTR slice 3b/3c) ---------------
  // All Bearer-gated; the server enforces that the caller claimed the profile.

  /** Correct a mis-scraped title/price. Only provided fields are written. */
  editMerchItem: async (
    platform: string, handle: string, itemId: string,
    fields: { title?: string; price_min?: number; price_max?: number },
    authToken?: string,
  ): Promise<{ item_id: string; updated: boolean }> => {
    const res = await fetch(
      `${base}/api/data/creator/${encodeURIComponent(platform)}/${encodeURIComponent(handle)}/merch/${encodeURIComponent(itemId)}`,
      { method: 'PATCH', headers: authJsonHeaders(authToken), body: JSON.stringify(fields) },
    );
    return handleResponse(res);
  },

  /** Remove an incorrect merch item. */
  deleteMerchItem: async (
    platform: string, handle: string, itemId: string, authToken?: string,
  ): Promise<{ item_id: string; deleted: boolean }> => {
    const res = await fetch(
      `${base}/api/data/creator/${encodeURIComponent(platform)}/${encodeURIComponent(handle)}/merch/${encodeURIComponent(itemId)}`,
      { method: 'DELETE', headers: authJsonHeaders(authToken) },
    );
    return handleResponse(res);
  },

  /** Connect a merch shop URL — scrapes its Shopify products. */
  addMerchShop: async (
    platform: string, handle: string, url: string, authToken?: string,
  ): Promise<{ shop_domain: string; items_added: number; message?: string }> => {
    const res = await fetch(
      `${base}/api/data/creator/${encodeURIComponent(platform)}/${encodeURIComponent(handle)}/merch-shop`,
      { method: 'POST', headers: authJsonHeaders(authToken), body: JSON.stringify({ url }) },
    );
    return handleResponse(res);
  },

  /** Disconnect a shop (removes its items) to correct a wrong link. */
  removeMerchShop: async (
    platform: string, handle: string, domain: string, authToken?: string,
  ): Promise<{ shop_domain: string; items_removed: number }> => {
    const res = await fetch(
      `${base}/api/data/creator/${encodeURIComponent(platform)}/${encodeURIComponent(handle)}/merch-shop?domain=${encodeURIComponent(domain)}`,
      { method: 'DELETE', headers: authJsonHeaders(authToken) },
    );
    return handleResponse(res);
  },

  getCreatorDetail: async (
    platform: string,
    handle: string,
    options?: {
      /** "Very Low" | "Low" | "Medium" | "High". Defaults server-side to "Low" so
       *  most legitimate matches still surface. Set to "Medium" to hide the
       *  noisier tail from the page; set to "Very Low" via the "More results"
       *  toggle to expose everything. */
      minConfidence?: string;
      /** When true, include rows the false-positive filter flagged
       *  (NON_BRAND_KEYS, "thanks to" misattributions, redirect-only URLs).
       *  Surfaced via the "Show low-confidence results" affordance. */
      includeFalsePositives?: boolean;
      /** When true, include the creator's own products (Chamberlain Coffee
       *  on Emma Chamberlain's profile, etc.). Default behaviour drops them
       *  so the page reads as a pitch-finding tool, not a self-promo
       *  showcase. */
      includeCreatorOwned?: boolean;
      /** Optional Clerk token — unlocks claimed_by_me in the response. */
      authToken?: string;
    },
  ): Promise<{
    creator: CreatorSummary;
    /** Cadence + top video from the stored recent-uploads window (0027). */
    video_insights?: VideoInsights | null;
    sponsorships: SponsorshipDetail[];
    /** Detected merch-shop entries, split out by the server's shop-vs-sponsor
     *  classifier so the profile renders them in a dedicated Shop section
     *  (never also in `sponsorships` — a row is in exactly one bucket). */
    shop?: SponsorshipDetail[];
    /** Total before confidence + false-positive filtering. */
    all_sponsorships_count?: number;
    /** Number filtered out at this min_confidence + include_false_positives. */
    filtered_out_count?: number;
    person_id?: string;
    other_platforms?: unknown[];
    /** True when an analysis is currently running for this (platform, handle).
     *  Set both on hits AND on the soft-404 stub (when the creator has no
     *  DB row yet but a job is in flight). Frontend shows a progress banner. */
    active_analysis_present?: boolean;
    /** True when a Bearri user has proven ownership of any of this person's
     *  platform accounts (OAuth connect). Drives the Bearri-creator badge
     *  and the "only the claimer links new platforms" affordances. */
    claimed?: boolean;
    claimed_by_me?: boolean;
    /** Real tally of signed-in users who voted to invite this creator. */
    invite_votes?: number;
    invited_by_me?: boolean;
  }> => {
    const params = new URLSearchParams();
    if (options?.minConfidence) params.set('min_confidence', options.minConfidence);
    if (options?.includeFalsePositives) params.set('include_false_positives', 'true');
    if (options?.includeCreatorOwned) params.set('exclude_creator_owned', 'false');
    const qs = params.toString() ? `?${params.toString()}` : '';
    const headers: Record<string, string> = {};
    if (options?.authToken) headers['Authorization'] = `Bearer ${options.authToken}`;
    const res = await fetch(
      `${base}${apiConfig.endpoints.dataCreators.replace('/creators', '')}/creator/${encodeURIComponent(platform)}/${encodeURIComponent(handle)}${qs}`,
      { headers },
    );
    return handleResponse(res);
  },

  /** Toggle the current user's "invite this creator to Bearri" vote.
   *  Auth required. Returns the authoritative new tally. */
  voteInviteCreator: async (
    platform: string,
    handle: string,
    authToken?: string,
  ): Promise<{ votes: number; voted: boolean }> => {
    const headers: Record<string, string> = {};
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    const res = await fetch(
      `${base}${apiConfig.endpoints.dataCreators.replace('/creators', '')}/creator/${encodeURIComponent(platform)}/${encodeURIComponent(handle)}/invite-vote`,
      { method: 'POST', headers },
    );
    return handleResponse(res);
  },

  /**
   * Look up the in-flight analysis state for a creator, if any.
   *
   * Returns ``{ found: false }`` when there is no active analysis. Otherwise
   * returns the live (platform, handle) entry with hydrated sub-job
   * progress — same shape across platforms so the profile-page banner
   * can render a single component regardless of whether it's a YouTube
   * scrape, an Instagram scrape, or both in parallel.
   *
   * Polled by ``CreatorProfile.tsx`` every ~2s while a job is active.
   * Stop polling once ``found`` flips to false (terminal hook fired).
   */
  getCreatorActiveAnalysis: async (
    platform: string,
    handle: string,
  ): Promise<{
    found: boolean;
    platform: string;
    handle: string;
    kind?: string;
    started_at?: string | null;
    sub_jobs?: Array<{
      platform: string;
      job_id: string;
      status?: string;
      is_complete?: boolean;
      progress?: Record<string, unknown> | null;
      error?: string | null;
    }>;
  }> => {
    const res = await fetch(
      `${base}${apiConfig.endpoints.dataCreators.replace('/creators', '')}/creator/${encodeURIComponent(platform)}/${encodeURIComponent(handle)}/active-analysis`,
    );
    return handleResponse(res);
  },

  refreshCreatorAvatar: async (
    platform: string,
    handle: string,
    authToken?: string,
  ): Promise<{ ok: boolean; avatar_url: string }> => {
    // Auth required: the endpoint triggers paid YouTube-API / Apify calls,
    // so it's Bearer-gated + rate-limited server-side. Callers must pass a
    // fresh Clerk token.
    const headers: Record<string, string> = {};
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    const res = await fetch(
      `${base}${apiConfig.endpoints.dataCreators.replace('/creators', '')}/creator/${encodeURIComponent(platform)}/${encodeURIComponent(handle)}/refresh-avatar`,
      { method: 'POST', headers },
    );
    return handleResponse(res);
  },

  getBrandDetail: async (
    key: string,
    options?: {
      minConfidence?: string;
      includeFalsePositives?: boolean;
      includeCreatorOwned?: boolean;
    },
  ): Promise<{
    brand: BrandSummary;
    sponsorships: SponsorshipDetail[];
    all_sponsorships_count?: number;
    filtered_out_count?: number;
  }> => {
    const params = new URLSearchParams();
    if (options?.minConfidence) params.set('min_confidence', options.minConfidence);
    if (options?.includeFalsePositives) params.set('include_false_positives', 'true');
    if (options?.includeCreatorOwned) params.set('exclude_creator_owned', 'false');
    const qs = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(
      `${base}${apiConfig.endpoints.dataBrands.replace('/brands', '')}/brand/${encodeURIComponent(key)}${qs}`,
    );
    return handleResponse(res);
  },

  getProspects: async (
    platform: string,
    handle: string,
    maxResults?: number,
    authToken?: string,
  ): Promise<{ creator: CreatorSummary; recommendations: ProspectRecommendation[]; usage_charged?: boolean; run_id?: string }> => {
    const params = maxResults ? `?max_results=${maxResults}` : '';
    const res = await fetch(
      `${base}${apiConfig.endpoints.dataProspects}/${encodeURIComponent(platform)}/${encodeURIComponent(handle)}${params}`,
      { headers: authJsonHeaders(authToken) },
    );
    return handleResponse(res, { quotaGated: true });
  },

  getMyProspects: async (
    profile: MyProspectsRequest,
    authToken?: string,
  ): Promise<{ profile: Record<string, unknown>; recommendations: ProspectRecommendation[] }> => {
    const res = await fetch(`${base}${apiConfig.endpoints.dataMyProspects}`, {
      method: 'POST',
      headers: authJsonHeaders(authToken),
      body: JSON.stringify(profile),
    });
    return handleResponse(res, { quotaGated: true });
  },

  getStats: async (): Promise<StatsResponse> => {
    const res = await fetch(`${base}${apiConfig.endpoints.dataStats}`);
    return handleResponse(res);
  },

  getCreatorRefreshStatus: async (platform: string, handle: string): Promise<CreatorRefreshStatus> => {
    const res = await fetch(`${base}/api/data/creator/${encodeURIComponent(platform)}/${encodeURIComponent(handle)}/refresh-status`);
    return handleResponse(res);
  },

  requestCreatorRefresh: async (platform: string, handle: string, authToken: string): Promise<CreatorRefreshStatus> => {
    const res = await fetch(`${base}/api/data/creator/${encodeURIComponent(platform)}/${encodeURIComponent(handle)}/refresh`,
      { method: 'POST', headers: { Authorization: `Bearer ${authToken}` } });
    return handleResponse(res);
  },

  seedCreator: async (
    platform: string,
    handle: string,
    name?: string,
    maxItems?: number,
    authToken?: string,
    targetCreatorId?: string,
  ): Promise<{
    // Async platforms (YouTube/Instagram) dispatch a background job:
    // {active: true, job_id, platform, handle, message, deduped?}. TikTok/
    // Twitch remain synchronous: {saved, creator, profile_linked, message}.
    message: string;
    active?: boolean;
    deduped?: boolean;
    job_id?: string;
    platform?: string;
    handle?: string;
    saved?: number;
    creator?: CreatorSummary;
    profile_linked?: boolean;
    refresh?: CreatorRefreshStatus;
    link?: CreatorPlatformLink;
  }> => {
    const params = new URLSearchParams();
    if (name) params.set('name', name);
    if (maxItems) params.set('max_items', String(maxItems));
    if (targetCreatorId) params.set('target_creator_id', targetCreatorId);
    const qs = params.toString() ? `?${params.toString()}` : '';
    // authToken is REQUIRED in practice: the backend /seed endpoint sits
    // behind Depends(get_current_user), which is Bearer-only (no cookie
    // fallback). The old fetch sent no Authorization header, so every
    // call 401'd — the profile Scan tab + card Refresh buttons were
    // silently broken. Callers must pass a fresh Clerk token.
    const headers: Record<string, string> = {};
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
    const res = await fetch(
      `${base}${apiConfig.endpoints.dataSeed}/${encodeURIComponent(platform)}/${encodeURIComponent(handle)}${qs}`,
      { method: 'POST', headers },
    );
    return handleResponse(res);
  },

  getCreatorLinks: async (creatorId: string, authToken: string): Promise<{ links: CreatorPlatformLink[] }> => {
    const res = await fetch(`${base}/api/data/creator-links/${encodeURIComponent(creatorId)}`,
      { headers: { Authorization: `Bearer ${authToken}` } });
    return handleResponse(res);
  },

  generatePitch: async (
    creatorPlatform: string,
    creatorHandle: string,
    brandName: string,
    pitchType: string = 'outreach',
    authToken?: string,
    /** Optional creator-supplied steering from Pitch Writer step 3. Each only
     *  shapes the LLM instruction — never fabricates creator/brand facts. */
    extras?: { angle?: string; personalNotes?: string; tone?: string; maxWords?: number },
  ): Promise<PitchResponse> => {
    const res = await fetch(`${base}${apiConfig.endpoints.dataPitch}`, {
      method: 'POST',
      headers: authJsonHeaders(authToken),
      body: JSON.stringify({
        creator_platform: creatorPlatform,
        creator_handle: creatorHandle,
        brand_name: brandName,
        pitch_type: pitchType,
        ...(extras?.angle ? { angle: extras.angle } : {}),
        ...(extras?.personalNotes ? { personal_notes: extras.personalNotes } : {}),
        ...(extras?.tone ? { tone: extras.tone } : {}),
        max_words: extras?.maxWords ?? 120,
      }),
    });
    return handleResponse(res, { quotaGated: true });
  },

  // ─── Sprint 9 / Track 3 — similar-to-brand + run/pitch history ──────
  getSimilarToBrandProspects: async (
    req: SimilarToBrandRequest,
    authToken?: string,
  ): Promise<{
    anchor_brand: { id?: string; name: string; category: string; logo_url?: string; brand_color?: string };
    creator: CreatorSummary | null;
    recommendations: ProspectRecommendationFull[];
    usage_charged?: boolean;
    run_id?: string;
    meta?: Record<string, unknown>;
  }> => {
    const res = await fetch(`${base}${apiConfig.endpoints.dataSimilarToBrand}`, {
      method: 'POST',
      headers: authJsonHeaders(authToken),
      body: JSON.stringify(req),
    });
    return handleResponse(res, { quotaGated: true });
  },

  getProspectsHistory: async (
    limit: number = 20,
    offset: number = 0,
    authToken?: string,
  ): Promise<{ runs: ProspectRunSummary[]; limit: number; offset: number }> => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (offset > 0) params.set('offset', String(offset));
    const res = await fetch(
      `${base}${apiConfig.endpoints.dataProspectsHistory}?${params.toString()}`,
      { headers: authJsonHeaders(authToken) },
    );
    return handleResponse(res);
  },

  getProspectRun: async (runId: string, authToken?: string): Promise<ProspectRunDetail> => {
    const res = await fetch(
      `${base}${apiConfig.endpoints.dataProspectRun}/${encodeURIComponent(runId)}`,
      { headers: authJsonHeaders(authToken) },
    );
    return handleResponse(res);
  },

  /** The signed-in user's outreach pipeline — every pitch as a staged card. */
  getOutreachPipeline: async (authToken?: string): Promise<{ cards: OutreachCard[] }> => {
    const res = await fetch(`${base}/api/outreach/pipeline`, {
      headers: authJsonHeaders(authToken),
    });
    return handleResponse(res);
  },

  /** The full Gmail conversation behind a tracked pitch (plain-text bodies).
   *  409 with reason gmail_not_connected when the token was revoked. */
  getOutreachThread: async (
    pitchId: string,
    authToken?: string,
  ): Promise<{ thread_id: string; messages: OutreachMessage[]; truncated?: boolean }> => {
    const res = await fetch(`${base}/api/outreach/${encodeURIComponent(pitchId)}/thread`, {
      headers: authJsonHeaders(authToken),
    });
    return handleResponse(res);
  },

  /** Reply INTO the tracked thread, sent as the creator from their own
   *  Gmail. Threads correctly in the brand's inbox (threadId + In-Reply-To
   *  + a matching "Re:" subject, all resolved server-side). 409 with reason
   *  gmail_insufficient_scope when the grant can't read the thread it needs
   *  to reply to. */
  replyInThread: async (
    pitchId: string,
    body: string,
    authToken?: string,
  ): Promise<OutreachReplyResult> => {
    const res = await fetch(`${base}/api/outreach/${encodeURIComponent(pitchId)}/reply`, {
      method: 'POST',
      headers: authJsonHeaders(authToken),
      body: JSON.stringify({ body }),
    });
    return handleResponse(res);
  },

  /** Recent sent threads from the user's own Gmail (default: last 60 days,
   *  already-tracked threads filtered out). q accepts Gmail search syntax. */
  getOutreachInbox: async (
    q: string,
    authToken?: string,
  ): Promise<{ threads: OutreachInboxThread[]; query: string }> => {
    const params = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
    const res = await fetch(`${base}/api/outreach/inbox${params}`, {
      headers: authJsonHeaders(authToken),
    });
    return handleResponse(res);
  },

  /** Pull an existing Gmail thread into the pipeline as a tracked card. */
  trackOutreachThread: async (
    threadId: string,
    brandName: string,
    authToken?: string,
  ): Promise<{ success: boolean; card: OutreachCard }> => {
    const res = await fetch(`${base}/api/outreach/track-thread`, {
      method: 'POST',
      headers: authJsonHeaders(authToken),
      body: JSON.stringify({ thread_id: threadId, brand_name: brandName }),
    });
    return handleResponse(res);
  },

  /** Advance a pipeline card: mark_sent | won | lost | reopen. A Won writes
   *  the creator×brand edge back into the sponsor graph server-side. */
  outreachAction: async (
    pitchId: string,
    action: 'mark_sent' | 'won' | 'lost' | 'reopen',
    authToken?: string,
    /** Optional, only with action="won". Omit entirely when the creator
     *  declines to share — the server stores NULL, never 0. */
    deal?: { amount: number; currency?: string; deliverables?: string },
  ): Promise<{ success: boolean; card: OutreachCard }> => {
    const res = await fetch(`${base}/api/outreach/${encodeURIComponent(pitchId)}/action`, {
      method: 'POST',
      headers: authJsonHeaders(authToken),
      body: JSON.stringify({
        action,
        ...(deal ? {
          deal_amount: deal.amount,
          deal_currency: deal.currency || 'USD',
          deal_deliverables: deal.deliverables || undefined,
        } : {}),
      }),
    });
    return handleResponse(res);
  },

  /** Peer reported-deal rates. `benchmark` is null until enough creators
   *  have reported — the UI must then say we don't know yet. */
  getRateBenchmark: async (
    authToken?: string,
  ): Promise<{ benchmark: null | {
    n: number; currency: string; low: number; median: number; high: number;
    tier: string | null; category: string | null;
  } }> => {
    const res = await fetch(`${base}/api/outreach/rate-benchmark`, {
      headers: authJsonHeaders(authToken),
    });
    return handleResponse(res);
  },

  getPitchHistory: async (
    limit: number = 50,
    offset: number = 0,
    authToken?: string,
  ): Promise<{ pitches: PastPitchSummary[]; limit: number; offset: number }> => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (offset > 0) params.set('offset', String(offset));
    const res = await fetch(
      `${base}${apiConfig.endpoints.dataPitchHistory}?${params.toString()}`,
      { headers: authJsonHeaders(authToken) },
    );
    return handleResponse(res);
  },

  getPitchDraft: async (id: string, authToken: string): Promise<PastPitchSummary> => {
    const res = await fetch(`${base}/api/outreach/${encodeURIComponent(id)}/draft`, { headers: authJsonHeaders(authToken) });
    return handleResponse(res);
  },

  /** Mint a hosted Creator Report share link (Plus+). Server returns the
   *  canonical share path; 402 = tier below Plus. */
  createReportShare: async (
    platform: string,
    handle: string,
    contactEmail: string,
    authToken?: string,
  ): Promise<{ token: string; share_url: string; expires_at: string }> => {
    const res = await fetch(`${base}/api/share/creator-report`, {
      method: 'POST',
      headers: authJsonHeaders(authToken),
      body: JSON.stringify({ platform, handle, contact_email: contactEmail }),
    });
    return handleResponse(res);
  },

  /** Resolve a public report share token — unauthenticated. */
  getPublicReport: async (
    token: string,
  ): Promise<{ report: PublicReportSnapshot; share: { created_at: string | null; expires_at: string | null } }> => {
    const res = await fetch(`${base}/api/public/creator-report/${encodeURIComponent(token)}`);
    return handleResponse(res);
  },

  getOutreachHints: async (
    brandName: string,
    brandDomain?: string,
    authToken?: string,
  ): Promise<OutreachHints> => {
    const res = await fetch(`${base}${apiConfig.endpoints.dataOutreachHints}`, {
      method: 'POST',
      headers: authJsonHeaders(authToken),
      body: JSON.stringify({
        brand_name: brandName,
        brand_domain: brandDomain,
      }),
    });
    return handleResponse(res);
  },
};

export default dataService;

// ─── Sprint 9 Phase 1 — Bulk scrape + creator-link standalone functions ─
//
// Exported as named functions (NOT methods on the dataService object) to
// match the API contract the founder spec'd. These all require a Clerk
// auth token because the operations are user-scoped (the bulk job is
// owned by the caller; link confirm/report writes user-attributed votes).

export const startBulkScrape = async (
  creators: Array<{ platform: string; handle: string }>,
  authToken: string,
): Promise<BulkScrapeStartResponse> => {
  const res = await fetch(`${base}/api/data/scrape-bulk`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ creators }),
  });
  // The server is the only gate: a plan below Plus comes back 403 tier_required, said by the page in its own words.
  if (res.status === 403 && res.headers?.get?.('X-Error-Type') === 'tier_required') {
    let msg = 'Checking channels in bulk is on Plus.';
    try { const body = await res.json(); if (typeof body?.detail === 'string') msg = body.detail; } catch { /* not JSON */ }
    throw new TierRequiredError(msg);
  }
  return handleResponse<BulkScrapeStartResponse>(res);
};

export const getBulkScrapeProgress = async (
  jobId: string,
  authToken: string,
): Promise<BulkScrapeProgress> => {
  const res = await fetch(
    `${base}/api/data/scrape-bulk/${encodeURIComponent(jobId)}/progress`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    },
  );
  return handleResponse<BulkScrapeProgress>(res);
};

export const confirmCreatorLink = async (
  sourceCreatorId: string,
  candidateCreatorId: string,
  decision: 'confirm' | 'reject',
  authToken: string,
): Promise<CreatorLinkConfirmResponse> => {
  const res = await fetch(`${base}/api/data/creator-link-confirm`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({
      source_creator_id: sourceCreatorId,
      candidate_creator_id: candidateCreatorId,
      decision,
    }),
  });
  return handleResponse<CreatorLinkConfirmResponse>(res);
};

export const reportCreatorLink = async (
  personId: string,
  reason: string,
  authToken: string,
): Promise<CreatorLinkReportResponse> => {
  const res = await fetch(`${base}/api/data/creator-link-report`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${authToken}`,
    },
    body: JSON.stringify({ person_id: personId, reason }),
  });
  return handleResponse<CreatorLinkReportResponse>(res);
};

export interface AdminUnlinkResponse {
  unlinked: boolean;
  creator_id: string;
  prior_person_id?: string;
  platform?: string;
  handle?: string;
  reason?: string;
}

/**
 * Admin-only single-creator unlink. Detaches ONE sibling from its Person
 * without dissolving the whole merge. Returns 403 for non-admin callers
 * — the caller should fall back to ``reportCreatorLink`` in that case
 * so end-users still get the soft-report path.
 */
export const adminUnlinkCreator = async (
  creatorId: string,
  reason: string | undefined,
  authToken: string,
): Promise<AdminUnlinkResponse> => {
  const res = await fetch(
    `${base}/api/data/admin/creators/${encodeURIComponent(creatorId)}/unlink`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`,
      },
      body: JSON.stringify({ creator_id: creatorId, reason: reason || null }),
    },
  );
  return handleResponse<AdminUnlinkResponse>(res);
};
